/**
 * FR-7: Simplified Onboarding
 * Single-step onboarding: company name + primary color only
 * Widget working in 30 seconds with smart defaults
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  Building2,
  Copy,
  Check,
  ExternalLink,
  Sparkles,
  Settings,
  Rocket,
  Palette,
} from 'lucide-react';
import { useAuthContext } from '@/components/auth/ClerkAuthProvider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { FormError } from '@/components/ui/form-error';
import { Separator } from '@/components/ui/separator';
import { createSlug } from '@/lib/utils';
import { DEFAULT_WIZARD_DATA } from '@/types/onboarding';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Simplified schema: only company name and primary color required
const simplifiedOnboardingSchema = z.object({
  companyName: z
    .string()
    .min(2, 'Company name must be at least 2 characters')
    .max(100, 'Company name must be less than 100 characters')
    .regex(/^[a-zA-Z0-9\s\-&'.]+$/, 'Company name contains invalid characters'),
  primaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Please enter a valid hex color'),
});

type SimplifiedOnboardingData = z.infer<typeof simplifiedOnboardingSchema>;

export function SimplifiedOnboardingWizard() {
  const navigate = useNavigate();
  const { user, userId, setTenantId } = useAuthContext();
  const { getToken } = useAuth();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdTenantSlug, setCreatedTenantSlug] = useState<string | null>(null);
  const [embedCopied, setEmbedCopied] = useState(false);

  // Cleanup timeout on unmount
  useEffect(() => {
    const currentTimeout = timeoutRef.current;
    return () => {
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
    };
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SimplifiedOnboardingData>({
    resolver: zodResolver(simplifiedOnboardingSchema),
    defaultValues: {
      companyName: '',
      primaryColor: DEFAULT_WIZARD_DATA.primaryColor,
    },
  });

  const companyName = watch('companyName');
  const primaryColor = watch('primaryColor');
  const slug = createSlug(companyName) || 'your-company';

  // Generate embed code for preview
  const widgetUrl = `${window.location.origin}/widget/${slug}`;
  const embedCode = `<iframe
  src="${widgetUrl}"
  width="100%"
  height="800"
  frameborder="0"
  allow="camera"
  style="max-width: 480px; border-radius: 12px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);"
></iframe>`;

  const copyEmbedCode = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setEmbedCopied(true);
      toast.success('Embed code copied!');
      setTimeout(() => setEmbedCopied(false), 2000);
    } catch {
      toast.error('Failed to copy embed code');
    }
  };

  const onSubmit = async (formData: SimplifiedOnboardingData) => {
    if (!user || !userId) {
      toast.error('You must be signed in to create an organization');
      return;
    }

    setIsSubmitting(true);

    try {
      // Generate slug from company name
      const tenantSlug = createSlug(formData.companyName);
      if (!tenantSlug) {
        toast.error('Invalid company name', {
          description: 'Company name must be at least 2 characters.',
        });
        setIsSubmitting(false);
        return;
      }

      // Get Clerk token for API authentication
      const token = await getToken();
      if (!token) {
        toast.error('Authentication error', {
          description: 'Please sign in again.',
        });
        setIsSubmitting(false);
        return;
      }

      // Create tenant with smart defaults
      const response = await fetch(`${API_BASE_URL}/tenants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          companyName: formData.companyName.trim(),
          slug: tenantSlug,
          primaryColor: formData.primaryColor,
          // FR-7: Smart defaults applied
          showQuote: DEFAULT_WIZARD_DATA.showQuote,
          requireAddress: DEFAULT_WIZARD_DATA.requireAddress,
          requireTimeline: DEFAULT_WIZARD_DATA.requireTimeline,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create organization');
      }

      if (!result.data?.id) {
        throw new Error('Server returned invalid response - missing tenant ID');
      }

      // Set tenant ID and show success state with embed code
      setTenantId(result.data.id);
      setCreatedTenantSlug(result.data.slug || tenantSlug);

      toast.success('Account created successfully!', {
        description: 'Your widget is ready to embed.',
      });
    } catch (error) {
      console.error('Error creating organization:', error);
      toast.error('Failed to create organization', {
        description: error instanceof Error ? error.message : 'Please try again or contact support.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  // Show success state with embed code after account creation
  if (createdTenantSlug) {
    const finalWidgetUrl = `${window.location.origin}/widget/${createdTenantSlug}`;
    const finalEmbedCode = `<iframe
  src="${finalWidgetUrl}"
  width="100%"
  height="800"
  frameborder="0"
  allow="camera"
  style="max-width: 480px; border-radius: 12px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);"
></iframe>`;

    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <Card className="border-0 shadow-lg">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
                <Rocket className="h-8 w-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl">Your widget is ready!</CardTitle>
              <CardDescription className="text-base">
                Copy the code below and paste it on your website.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6 pt-4">
              {/* Embed Code */}
              <div className="space-y-3">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  Embed Code
                </h3>
                <div className="relative">
                  <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-all">
                    {finalEmbedCode}
                  </pre>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="absolute top-2 right-2"
                    onClick={copyEmbedCode}
                  >
                    {embedCopied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(finalWidgetUrl, '_blank')}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Preview Widget
                </Button>
              </div>

              <Separator />

              {/* Next Steps */}
              <div className="rounded-lg bg-primary/5 border border-primary/20 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <h3 className="font-medium">What's Next?</h3>
                </div>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">1.</span>
                    Copy the embed code and paste it on your website
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">2.</span>
                    Test the widget by submitting a lead
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">3.</span>
                    View leads in your dashboard
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 pt-2">
                <Button
                  onClick={goToDashboard}
                  size="lg"
                  className="w-full h-12 text-base"
                >
                  Go to Dashboard
                </Button>

                {/* FR-7: 'Customize later' link to full settings */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="mx-auto text-muted-foreground"
                  onClick={() => navigate('/settings/widget')}
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Customize later
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main onboarding form - single step
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Card className="border-0 shadow-lg">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                <Building2 className="h-7 w-7 text-primary" />
              </div>
              <CardTitle className="text-2xl">Set up your widget</CardTitle>
              <CardDescription className="text-base">
                Just two quick fields and you're ready to capture leads.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6 pt-4">
              {/* Company Name */}
              <div className="space-y-2">
                <Label htmlFor="companyName" className="text-base font-medium">
                  Company Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="companyName"
                  {...register('companyName')}
                  placeholder="Premier Renovations"
                  className={`h-12 text-base ${errors.companyName ? 'border-destructive' : ''}`}
                  autoFocus
                />
                <FormError message={errors.companyName?.message} />
                {companyName && companyName.length >= 2 && (
                  <p className="text-sm text-muted-foreground">
                    Widget URL: <code className="text-xs bg-muted px-1 py-0.5 rounded">/widget/{slug}</code>
                  </p>
                )}
              </div>

              {/* Primary Color */}
              <div className="space-y-2">
                <Label htmlFor="primaryColor" className="text-base font-medium flex items-center gap-2">
                  <Palette className="h-4 w-4" />
                  Brand Color
                </Label>
                <div className="flex gap-3">
                  <Input
                    id="primaryColor"
                    type="color"
                    {...register('primaryColor')}
                    className="h-12 w-16 p-1 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={primaryColor}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                        register('primaryColor').onChange(e);
                      }
                    }}
                    placeholder="#2563eb"
                    className={`h-12 flex-1 font-mono ${errors.primaryColor ? 'border-destructive' : ''}`}
                  />
                </div>
                <FormError message={errors.primaryColor?.message} />
                <p className="text-sm text-muted-foreground">
                  This color will be used for buttons and accents in your widget.
                </p>
              </div>

              <Separator />

              {/* Smart Defaults Info */}
              <div className="rounded-lg bg-muted/50 p-4 space-y-2">
                <p className="text-sm font-medium">Smart defaults applied:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Show price estimates to leads</li>
                  <li>• Minimal lead fields (name, email, phone)</li>
                  <li>• CTA: "Get Your Free Design"</li>
                </ul>
                <p className="text-xs text-muted-foreground pt-1">
                  You can customize these in Settings after setup.
                </p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                size="lg"
                className="w-full h-12 text-base"
              >
                {isSubmitting ? (
                  <>Creating your account...</>
                ) : (
                  <>
                    <Rocket className="mr-2 h-5 w-5" />
                    Create Widget
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
}
